import { Component, ViewChild, ElementRef } from '@angular/core';
import { BaseComponent } from '@varsity/components';
import {
    LoggingService, TokenService, LocalStorageService,
    NotificationsService, AccountService,
    UserCreate, PlatformService, ValidationService,
    NotificationToken, SecureStorageService,
    DeviceInfo
} from "@varsity/services";
import { ViewController, ToastController, PopoverController, NavParams } from 'ionic-angular';

import { DisplayMessageComponent } from '../display-message/display-message';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import LogRocket from 'logrocket';

/**
 * Generated class for the LoginComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
    selector: 'login',
    templateUrl: 'login.html'
})
export class LoginComponent extends BaseComponent {

    @ViewChild('inputConfirmUserId', { read: ElementRef }) inputConfirmUserId: ElementRef;
    @ViewChild('inputConfirmPassword', { read: ElementRef }) inputConfirmPassword: ElementRef;
    @ViewChild('inputMobileNumber', { read: ElementRef }) inputMobileNumber: ElementRef;

    userId: string = "";
    confirmUserId: string = "";
    password: string = "";
    confirmPassword: string = "";
    firstName: string = "";
    lastName: string = "";
    mobileNumber: string = "";
    foundAccount: boolean = false;
    accountExists: boolean = false;
    errors: string = "";
    biometricInfo: { email: string, password: string } | boolean = false;


    constructor(
        protected loggingService: LoggingService,
        protected tokenService: TokenService,
        protected localStorageService: LocalStorageService,
        protected notificationsService: NotificationsService,
        protected accountService: AccountService,
        protected toastCtrl: ToastController,
        protected viewCtrl: ViewController,
        protected popoverCtrl: PopoverController,
        protected platformService: PlatformService,
        protected validationService: ValidationService,
        protected secureStorageService: SecureStorageService,
        protected navParams: NavParams,
        protected iab: InAppBrowser,
    ) {
        super(
            loggingService, tokenService, localStorageService,
            notificationsService, platformService, toastCtrl, popoverCtrl, iab,
            "LoginComponent"
        );

    }

    ngOnInit() {
        super.ngOnInit();
        this.secureStorageService.init().then((result) => {
            if (result) {

                this.HasCredentials().then((res) => {
                    this.biometricInfo = res;
                    // start login with biometrics if userid is still empty
                    if (this.biometricInfo && this.userId == "") {
                        this.LoginWithBiometric();
                    }
                }, (error) => {

                });
            }
        });

        // nice example for debounce
        // https://stackoverflow.com/questions/32051273/angular-and-debounce
        this.userId = this.navParams.get('email') || "";
    }

    Cancel(recover: boolean = false) {
        if (recover) {
            this.viewCtrl.dismiss({
                recover: recover,
                email: this.userId,
                updateRequired: false
            });
        } else {
            this.viewCtrl.dismiss({
                updateRequired: false
            });
        }
    }

    Back() {
        this.foundAccount = false;
        this.accountExists = false;
    }

    CheckAccount() {
        this.tokenService.ensureValidToken().then((token) => {
            if (this.validationService.emailIsValid(this.userId)) {
                this.accountService.TRLogin(this.userId, "", true, {})
                    .takeUntil(this.componentDestroyed$)
                    .finally(() => {
                        this.foundAccount = true;
                    })
                    .subscribe((user) => {
                        this.loggingService.debug(user);
                        this.accountExists = user ? true : false;

                        if (this.accountExists) {
                            this.showNotification('We have found your account. Please enter password to login.', undefined, undefined, "notification");
                        } else {
                            this.showNotification('We did not find your account. Please enter a password to create a new account.', undefined, undefined, "warning");
                        }
                    },
                        (error) => {
                            this.loggingService.debug(error);
                            //this.handleError(error);
                            this.showNotification('We did not find your account. Please enter a password to create a new account.', undefined, undefined, "warning");
                        });
            } else {
                this.showNotification('Please provide a valid email address.', undefined, undefined, "warning");
            }
        });
    }

    LoginWithBiometric() {
        if (this.secureStorageService.isInitialized()) {
            if (this.biometricInfo) {
                this.userId = (this.biometricInfo as { email: string, password: string }).email;
                this.password = (this.biometricInfo as { email: string, password: string }).password;
                this.Login(true);
            }
        }
    }

    Login(usingBiometric: boolean = false) {
        if (!this.password || !this.password.trim()) {
            this.showNotification("Password cannot be empty", undefined, undefined, "warning");
        } else {
            this.tokenService.ensureValidToken().then((token) => {
                let notificationTokenData: NotificationToken = {};
                notificationTokenData.EmailAddress = this.userId;
                this.platformService.notificationToken$
                    .take(1)
                    .subscribe(data => {
                        notificationTokenData.Token = data;
                        notificationTokenData.Version = (window as any).__GlobalSettings.Minor;
                        notificationTokenData.Product = "VarsityTix";

                        this.platformService.deviceInfo$
                            .take(1)
                            .subscribe(data => {
                                notificationTokenData.Device = new DeviceInfo();
                                notificationTokenData.Device.cordova = data.cordova;
                                notificationTokenData.Device.isVirtual = data.isVirtual;
                                notificationTokenData.Device.manufacturer = data.manufacturer;
                                notificationTokenData.Device.model = data.model;
                                notificationTokenData.Device.platform = data.platform;
                                notificationTokenData.Device.serial = data.serial;
                                notificationTokenData.Device.uuid = data.uuid;
                                notificationTokenData.Device.version = data.version;

                                if (this.validationService.emailIsValid(this.userId)) {
                                    this.CallLogin(notificationTokenData, usingBiometric);
                                } else {
                                    this.showNotification('Please provide a valid email address.', undefined, undefined, "warning");
                                }
                            });
                    });
            });
        }
    }

    HasCredentials(): Promise<{ email: string, password: string } | boolean> {
        var promise = new Promise<{ email: string, password: string } | boolean>((resolve, reject) => {
            this.GetCredentials().then((secrets) => {
                if (secrets) {
                    resolve(secrets);
                } else {
                    resolve(false);
                }
            }).catch((reason) => {
                reject(reason);
            });
        });

        return promise;
    }

    GetCredentials(): Promise<{ email: string, password: string }> {
        var promise = new Promise<{ email: string, password: string }>((resolve, reject) => {
            this.secureStorageService.get("varsity-credentials").then((value) => {
                var credentials: { email: string, password: string } = JSON.parse(value);
                resolve(credentials);
            }, (error) => {
                reject(error);
            });
        });

        return promise;
    }

    StoreCredentials(): Promise<boolean> {
        var promise = new Promise<boolean>((resolve, reject) => {
            this.secureStorageService.add("varsity-credentials",
                JSON.stringify({ email: this.userId, password: this.password }))
                .then((key) => {
                    this.loggingService.debug("key set");
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                })
        });

        return promise;
    }

    CallLogin(notificationTokenData: NotificationToken, usingBiometric: boolean = false) {
        this.accountService.TRLogin(this.userId, this.password, false, notificationTokenData)
            .takeUntil(this.componentDestroyed$)
            .subscribe((user) => {
                if (user) {
                    this.loggingService.debug(user);
                    this.localStorageService.add('user', JSON.stringify(user));
                    this.notificationsService.notifyUserLoginChanged(user);
                    LogRocket.identify(user.OwnerID.toString(), {
                        name: `${user.FirstName} ${user.LastName}`,
                        email: user.EmailAddress
                    })

                    this.accountService.getUserNotificationsCount()
                        .takeUntil(this.componentDestroyed$)
                        .subscribe((value) => {
                            this.notificationsService.notifyAvailableNotificationsChanged(value || 0);
                        });

                    //this.viewCtrl.dismiss();


                    if (!usingBiometric) {

                        // if we are not using biometric, we can ask to save them
                        if (this.secureStorageService.isInitialized()) {
                            // it is cordova
                            super.showPopover(DisplayMessageComponent, {
                                icon: "finger-print",
                                header: "Save vault biometrics",
                                content: ["Do you want to store your biometric credentials in the vault?"],
                                buttons: [{ tag: 'no', text: 'No' }, { tag: 'yes', text: 'Yes' }],
                                footer: null
                            }, 'display-message', (data) => {
                                if (data.button && data.button.tag == 'yes') {
                                    this.StoreCredentials().then(() => {
                                        this.showNotification("Biometrics saved in vault", undefined, undefined, "success");
                                        this.viewCtrl.dismiss({
                                            updateRequired: user.UpdateFlag,
                                            text: user.UpdateText
                                        });
                                    }).catch(() => {
                                        this.showNotification("Failed to save biometrics to vault", undefined, undefined, "error");
                                        this.viewCtrl.dismiss({
                                            updateRequired: user.UpdateFlag,
                                            text: user.UpdateText
                                        });
                                    });
                                } else {
                                    this.viewCtrl.dismiss({
                                        updateRequired: user.UpdateFlag,
                                        text: user.UpdateText
                                    });
                                }
                            });
                        } else {
                            this.viewCtrl.dismiss({
                                updateRequired: user.UpdateFlag,
                                text: user.UpdateText
                            });
                        }
                    } else {
                        this.viewCtrl.dismiss({
                            updateRequired: user.UpdateFlag,
                            text: user.UpdateText
                        });
                    }
                } else {
                    this.showNotification("Your user name and/or password do not match", undefined, undefined, "error");
                    this.password = "";
                }
            },
                (error) => {
                    this.password = "";
                    this.handleError(error);
                });
    }

    onFocusOutConfirmatationEmail() {
        if (this.confirmUserId !== "") {
            if (this.confirmUserId && this.validationService.emailIsValid(this.confirmUserId)) {
                if (this.userId.trim() !== this.confirmUserId.trim()) {
                    this.errors = 'Email Addresses do not match please try again.';
                    this.showNotification('Email Addresses do not match please try again.', undefined, undefined, "error");
                    this.AddRedLineOnValidation(this.inputConfirmUserId);
                    return;
                }
            }
            else {
                this.errors = 'Please provide a valid confirmation email address.';
                this.showNotification('Please provide a valid confirmation email address.', undefined, undefined, "error");
                this.AddRedLineOnValidation(this.inputConfirmUserId);
                return;
            }
        } else {
            this.errors = 'Please provide confirmation email.'
            this.showNotification('Please provide confirmation email.', undefined, undefined, "error");
            this.AddRedLineOnValidation(this.inputConfirmUserId);
            return;
        }

        this.errors = '';
        this.AddGreenLineOnValidation(this.inputConfirmUserId);
        return;
    }

    onFocusOutConfirmatationPassword() {
        if (this.confirmPassword !== "") {
            if (this.password.trim() !== this.confirmPassword.trim()) {
                this.errors = 'Passwords do not match.';
                this.showNotification('Passwords do not match.', undefined, undefined, "error");
                this.AddRedLineOnValidation(this.inputConfirmPassword);
                return;
            }
        } else {
            this.showNotification('Please provide confirmation password.', undefined, undefined, "error");
            this.errors = 'Please provide confirmation password.';
            this.AddRedLineOnValidation(this.inputConfirmPassword);
            return;
        }

        this.errors = '';
        this.AddGreenLineOnValidation(this.inputConfirmPassword);
        return;
    }

    onFocusOutMobileNumber() {
        if (this.mobileNumber !== "") {
            let valid = this.accountService.validatePhoneNumber(this.mobileNumber);
            if (!valid.success) {
                this.showNotification(valid.error, undefined, undefined, "error");
                this.errors = valid.error;
                return;
            }
        } else {
            this.showNotification('Please provide mobile number.', undefined, undefined, "error");
            this.errors = 'Please provide mobile number.';
            return;
        }

        this.errors = '';
        return;
    }

    AddRedLineOnValidation(inputElement) {
        inputElement.nativeElement.classList.remove("ng-valid");
        inputElement.nativeElement.classList.remove("ng-untouched");

        inputElement.nativeElement.classList.add("ng-touched");
        inputElement.nativeElement.classList.add("ng-invalid");
    }

    AddGreenLineOnValidation(inputElement) {
        inputElement.nativeElement.classList.remove("ng-invalid");
        inputElement.nativeElement.classList.remove("ng-touched");

        inputElement.nativeElement.classList.add("ng-untouched");
        inputElement.nativeElement.classList.add("ng-valid");
    }

    CreateAccount() {
        if (this.firstName !== undefined && this.lastName !== undefined && this.userId !== undefined && this.confirmUserId !== undefined && this.password !== undefined && this.mobileNumber !== undefined) {
            let valid = this.accountService.validatePhoneNumber(this.mobileNumber);

            if (!this.password.trim() || this.password.trim() != this.confirmPassword.trim()) {
                this.showNotification('Passwords do not match', undefined, undefined, "error");

            }
            else if (!this.confirmUserId && !this.validationService.emailIsValid(this.confirmUserId)) {
                this.showNotification('Please provide a valid confirmation email address.', undefined, undefined, "warning");
            }
            else if (this.userId.trim() !== this.confirmUserId.trim()) {
                this.showNotification('Emails do not match', undefined, undefined, "error");
            }
            else if (!valid.success) {
                this.showNotification(valid.error, undefined, undefined, "error");
                this.errors = valid.error;
                this.AddRedLineOnValidation(this.inputMobileNumber);
                return;
            }
            else {
                this.AddGreenLineOnValidation(this.inputMobileNumber);
                this.errors = '';
                this.tokenService.ensureValidToken().then((token) => {
                    let user = new UserCreate();
                    user.FirstName = this.firstName;
                    user.LastName = this.lastName;
                    user.UserID = this.userId;
                    user.Password = this.password;
                    user.MobileNumber = this.mobileNumber;

                    this.accountService.createCustomer(user)
                        .takeUntil(this.componentDestroyed$)
                        .subscribe((result) => {
                            this.loggingService.debug(result);
                            if (result.ResultMessage) {
                                if (!result["ResultFlag"]) {
                                    this.showNotification(result.ResultMessage, undefined, undefined, "error");
                                } else {
                                    this.showNotification(result.ResultMessage, undefined, undefined, "success");
                                    // now login the user
                                    this.Login();
                                }
                                return;
                            }
                        },
                            (error) => {
                                this.handleError(error);
                            });

                });
            }
        } else {
            this.showNotification('Please provide information for each field.', undefined, undefined, "error");
        }

    }

    RecoverPassword() {
        this.Cancel(true);
    }
}



// WEBPACK FOOTER //
// ./src/components/login/login.ts